// THE BOUNDS every hive payload is cut and validated against, and the three zod field
// builders that apply them.
// Split out of `../hive-event.ts`, which is now the barrel over this directory. The four
// caps, `HiveCutBound` and `hiveCut` are on the barrel; `count`, `line` and `repoPath` are
// exported only so the lane modules beside this one can build their fields, and stay off
// it — they are a FIELD SPELLING, not a contract anyone outside this directory reads.

import { z } from 'zod';

/**
 * Per-list cap in any hive payload. A 50-file manifest or a 32-reviewer fan-out must not
 * bloat the audit chain, and every capped list ships an explicit `*_truncated` count
 * beside it — a silently shortened audit line reads as a complete one.
 *
 * ONE cap for every lane on purpose. The producers had two (the scout capped at 10, the
 * team at 20), which meant "ten failures" meant a different thing on each lane and a
 * cross-lane reader had to know which.
 */
export const HIVE_EVENT_MAX_ITEMS = 20;

/** Chars of a question, a reason or a premise that ride an event. */
export const HIVE_TEXT_MAX_CHARS = 200;

/** Chars of a failed member's output excerpt. Longer than {@link HIVE_TEXT_MAX_CHARS}
 * because an excerpt is a stack tail, not a sentence. */
export const HIVE_EXCERPT_MAX_CHARS = 400;

/** Chars of a repo-relative path. */
export const HIVE_PATH_MAX_CHARS = 400;

/** The bound {@link hiveCut} cuts to. An OBJECT, and that is the whole point — see below. */
export type HiveCutBound = { readonly max: number };

/**
 * Truncate text to a bound INCLUDING the ellipsis, so a producer cannot slice to a
 * length its own schema then refuses.
 *
 * This exists because the producers did the arithmetic themselves and got it wrong in the
 * same direction every time: `${text.slice(0, MAX)}…` is `MAX + 1` characters, so a
 * schema bound of `MAX` would reject exactly the strings the producer meant to fit. One
 * seam for the bound and the cut, so there is one place to look and one place to fix.
 *
 * THE BOUND IS AN OBJECT BECAUSE A BARE `number` WAS A `map` FOOTGUN, AND IT FIRED.
 * `Array.prototype.map` calls back with `(value, index, array)`, so `paths.map(hiveCut)`
 * handed the INDEX in as the bound and `'src/ghost.ts:9'` came back as `'src/ghost.ts:…'`
 * — index 0, every element truncated to nothing but an ellipsis. A test caught it red,
 * which is the system working, but the SIGNATURE invited it and the next caller writes the
 * same line. With `{ max }` the mistake cannot compile (a `number` is not a
 * {@link HiveCutBound}), and on the runtime path a stray positional argument reads
 * `undefined` off a non-object and falls back to the default bound rather than mangling
 * the value. Fixing the pattern, not the instance.
 *
 * A bound below 2 THROWS, naming the value. That is the same defect arriving through a
 * COMPUTED bound, which no signature can refuse, and the floor is a property rather than a
 * tuned number: under 2 there is no room for a single character beside the ellipsis, so the
 * result is the ellipsis ALONE — a deletion wearing a truncation's shape. It is also
 * exactly what `map` indices 0 and 1 produce.
 *
 * THE RULE IS NOW ALSO IN `packages/kit` AS `ellipsize(text, { max })`, and this is
 * deliberately still a second copy rather than a wrapper over it. Both are pinned by
 * `scripts/one-impl.test.ts`'s `ellipsis-cut` row, which counts them and says why: this
 * package declares no internal dependency — its manifest has only `zod` and its
 * `CLAUDE.md` says "Imports nothing internal" — so delegating opens a
 * `@developerz/domain` -> `@developerz/kit` edge. Nothing forbids that edge (kit imports
 * no workspace, by construction), but opening it is a boundary decision for whoever owns
 * this package's manifest, not a side effect of a truncation cleanup. Until then the two
 * must not drift: they are the same three lines, and a change to either belongs in both.
 */
export function hiveCut(text: string, bound?: HiveCutBound): string {
  const max = bound?.max ?? HIVE_TEXT_MAX_CHARS;
  if (!Number.isInteger(max) || max < 2) {
    throw new Error(
      `hiveCut: max must be an integer >= 2, got ${max}. Under 2 there is no room for a ` +
        `character beside the ellipsis, so every value is deleted rather than cut; the ` +
        `usual cause is a positional argument where { max } was owed.`,
    );
  }
  return text.length <= max ? text : `${text.slice(0, max - 1)}…`;
}

/** A count on a hive event: a plain non-negative int, always present, zero included. */
export const count = () => z.number().int().nonnegative();

/** A bounded, non-empty line of text on a hive event. */
export const line = (max: number = HIVE_TEXT_MAX_CHARS) => z.string().trim().min(1).max(max);

/** A repo-relative path a fan-out declared, failed or left unread. */
export const repoPath = () => z.string().trim().min(1).max(HIVE_PATH_MAX_CHARS);
